import React from 'react';
import _ from 'underscore';

import {GridWithMinimalCells, GridCellSummary, isInaccessibleStorable} from 'constants/SampleConstants';
import notifications from 'notifications';
import GridCells from 'samples/grids/GridCells';

/*
 * Compare the given cell's coordinates with the given row and column indices.
 */
export function checkCoordinates(cell: GridCellSummary, {rowIndex, columnIndex}: GridCellSummary): boolean {
  return cell.rowIndex === rowIndex && cell.columnIndex === columnIndex;
}

/*
 * Check if an existing cell in `selectedCells` has the same row and column indices as the given new
 * indices.
 */
export function isCellSelected(selectedCells: Array<GridCellSummary>, newCell: GridCellSummary): boolean {
  return selectedCells.some((cell: GridCellSummary) => checkCoordinates(cell, newCell));
}

/*
 * Given a list of selected cells and a new cell, either remove the new cell from `selectedCells` if it
 * exists, or add it to the list of selected cells if it is not already in the list.
 */
export function toggleCellSelection(
  selectedCells: Array<GridCellSummary>,
  newCell: GridCellSummary
): Array<GridCellSummary> {
  if (isCellSelected(selectedCells, newCell)) {
    // eslint-disable-next-line no-restricted-syntax
    return _.reject(selectedCells, (cell) => checkCoordinates(cell, newCell));
  } else {
    return selectedCells.concat(newCell);
  }
}

/*
 * Given a list of grid cells and a position range, returns the grid cells (or coordinates for empty cells)
 * that fall within the selection range.
 */
export function getCellsInRange(
  gridCells: Array<Array<GridCellSummary | null>>,
  startCoordinates: GridCellSummary,
  endCoordinates: GridCellSummary
): Array<GridCellSummary> {
  const minRowIndex = Math.min(startCoordinates.rowIndex, endCoordinates.rowIndex);
  const maxRowIndex = Math.max(startCoordinates.rowIndex, endCoordinates.rowIndex);
  const minColIndex = Math.min(startCoordinates.columnIndex, endCoordinates.columnIndex);
  const maxColIndex = Math.max(startCoordinates.columnIndex, endCoordinates.columnIndex);

  const flattenedGridCells: Array<GridCellSummary> = [];
  gridCells.forEach((gridCellRow, rowIndex) => {
    gridCellRow.forEach((gridCell, columnIndex) => {
      if (gridCell) {
        flattenedGridCells.push(gridCell);
        // For empty grid cells, return {rowIndex, columnIndex}.
      } else {
        flattenedGridCells.push({rowIndex, columnIndex});
      }
    });
  });
  // eslint-disable-next-line no-restricted-syntax
  return _.filter(
    flattenedGridCells,
    (cell) =>
      minRowIndex <= cell.rowIndex &&
      cell.rowIndex <= maxRowIndex &&
      minColIndex <= cell.columnIndex &&
      cell.columnIndex <= maxColIndex
  );
}

/*
 * Given a list of selected cells and a list of new cells, add the new cells to the list of selected cells
 * if they are not already in the selected list.
 */
export function selectCells(
  selectedCells: Array<GridCellSummary>,
  cellsToSelect: Array<GridCellSummary>,
  checkCell: (grid: GridWithMinimalCells, cells: GridCellSummary) => boolean,
  grid: GridWithMinimalCells
): Array<GridCellSummary> {
  const newSelectedCells = selectedCells.slice();
  cellsToSelect.forEach((newCell: GridCellSummary) => {
    if (!isCellSelected(selectedCells, newCell) && checkCell(grid, newCell)) {
      newSelectedCells.push(newCell);
    }
  });
  return newSelectedCells;
}

/*
 * Given a list of selected cells and a list of new cells, remove the new cells from the list of selected
 * cells if they are in the selected list.
 */
export function deselectCells(
  selectedCells: Array<GridCellSummary>,
  cellsToDeselect: Array<GridCellSummary>
): Array<GridCellSummary> {
  // eslint-disable-next-line no-restricted-syntax
  return _.reject(selectedCells, (cell) => isCellSelected(cellsToDeselect, cell));
}

/**
 * Given a grid and a cell's rowIndex and columnIndex, return whether the cell is empty.
 *
 * In any cases where !isCellEmpty would be used, isCellOccupiedAndAccessible should probably be used instead
 * to ensure inaccessible containers are not selected.
 */
export function isCellEmpty(grid: GridWithMinimalCells, {rowIndex, columnIndex}: GridCellSummary): boolean {
  return !grid.cells[rowIndex][columnIndex];
}

/**
 * Given a grid and a rowIndex and columnIndex, return whether that cell has an accessible container.
 */
export function isCellOccupiedAndAccessible(
  grid: GridWithMinimalCells,
  {rowIndex, columnIndex}: GridCellSummary
): boolean {
  const cell = grid.cells[rowIndex][columnIndex];
  return cell != null && !isInaccessibleStorable(cell.container);
}

export interface GridCellsSelectorProps {
  allowSelectingCells?: boolean;
  canSelectMultiple?: boolean;
  checkCell?: (grid: GridWithMinimalCells, cell: GridCellSummary) => boolean;
  className?: string;
  grid: GridWithMinimalCells;
  invalidCellMessage?: string;
  isCompact?: boolean;
  onSelectCells?: (cells: Array<GridCellSummary>) => void;
  selectedCells: Array<GridCellSummary>;
  gridCellTooltipTextByPosition?: {[gridPosition: string]: string};
}

interface State {
  anchorCellCoordinates: GridCellSummary | null;
}

/**
 * Component that renders GridCells for selection with shift-click support.
 */
class GridCellsSelector extends React.Component<GridCellsSelectorProps, State> {
  static defaultProps = {
    allowSelectingCells: true,
    canSelectMultiple: true,
    invalidCellMessage: 'Position cannot be selected.',
    isCompact: true,
  };

  state: State = {
    // `anchorCellCoordinates` is the first selected cell position from which to start the selection range
    // for shift-click
    anchorCellCoordinates: null,
  };

  componentDidMount(): void {
    if (this.props.allowSelectingCells) {
      // Prevent dispatch within dispatch
      _.defer(() => {
        // Check `selectedCells` and discard the invalid ones:
        //   - cells that are out of `grid`'s bounds
        //   - cells that fail `checkCell`
        // Filter out of bounds cells BEFORE prop-provided checkCell,
        // since checkCell might break if cell is out of bounds
        const validSelectedCells = this.props.selectedCells
          .filter(
            (cell) =>
              cell.rowIndex < this.props.grid.storableSchema.height &&
              cell.columnIndex < this.props.grid.storableSchema.width
          )
          .filter((cell) => this.props.checkCell!(this.props.grid, cell));
        this.props.onSelectCells!(validSelectedCells);
      });
    }
  }

  private handleClickCell = (cell: GridCellSummary): void => {
    if (!this.props.checkCell!(this.props.grid, cell)) {
      // invalidCellMessage has a default value, so this should never be undefined here.
      notifications.warn(this.props.invalidCellMessage!);
      return;
    }
    if (this.props.selectedCells.length > 0 && !this.props.canSelectMultiple) {
      this.props.onSelectCells!([cell]);
      return;
    }
    const newSelectedCells = toggleCellSelection(this.props.selectedCells, cell);
    this.props.onSelectCells!(newSelectedCells);
    this.setState({anchorCellCoordinates: {rowIndex: cell.rowIndex, columnIndex: cell.columnIndex}});
  };

  private handleShiftClickCell = (cell: GridCellSummary): void => {
    if (!this.props.checkCell!(this.props.grid, cell) || !this.props.canSelectMultiple) {
      return;
    }
    let newSelectedCells;
    const clickCoordinates = {rowIndex: cell.rowIndex, columnIndex: cell.columnIndex};
    const anchorCoordinates = this.state.anchorCellCoordinates || {rowIndex: 0, columnIndex: 0};
    const rangeCells = getCellsInRange(this.props.grid.cells, anchorCoordinates, clickCoordinates);
    if (isCellSelected(this.props.selectedCells, cell)) {
      // Deselect the cells in range
      newSelectedCells = deselectCells(this.props.selectedCells, rangeCells);
    } else {
      // Select the cells in the range
      newSelectedCells = selectCells(
        this.props.selectedCells,
        rangeCells,
        this.props.checkCell!,
        this.props.grid
      );
    }
    this.props.onSelectCells!(newSelectedCells);
  };

  render(): JSX.Element {
    return (
      <GridCells
        className={this.props.className}
        grid={this.props.grid}
        isCompact={this.props.isCompact}
        onClickCell={this.props.allowSelectingCells ? this.handleClickCell : null}
        onShiftClickCell={this.props.allowSelectingCells ? this.handleShiftClickCell : null}
        selectedCells={this.props.selectedCells}
        gridCellTooltipTextByPosition={this.props.gridCellTooltipTextByPosition}
      />
    );
  }
}

export default GridCellsSelector;
